/**
 * VAROTRADES · Customer Activity + Website Journey tracker.
 *
 *  - trackEvent(eventType, extras?)             fire an event now
 *  - trackPageView(route, name?)                page_view (+ page_exit on next nav)
 *  - flush()                                    force-flush the queue
 *
 * Every event is stored in sessionStorage as a queue and flushed in batches
 * every FLUSH_INTERVAL ms (or on beforeunload via keepalive fetch). The
 * backend proxy at /api/track/activity signs the batch with the CRM secret.
 */
const SESSION_KEY  = 'vt.session_id';
const QUEUE_KEY    = 'vt.event_queue';
const FLUSH_INTERVAL = 30_000;
const MAX_QUEUE = 200;

const API_BASE = (typeof process !== 'undefined' && process.env.REACT_APP_BACKEND_URL) || '';
const TRACK_URL = `${API_BASE}/api/track/activity`;

// ─── session id ─────────────────────────────────────────────────────
const _uuid = () =>
  (crypto.randomUUID?.() || `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

function getSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) { sid = _uuid(); sessionStorage.setItem(SESSION_KEY, sid); }
    return sid;
  } catch { return `mem-${Date.now()}`; }
}

// ─── queue ──────────────────────────────────────────────────────────
function readQueue() {
  try   { return JSON.parse(sessionStorage.getItem(QUEUE_KEY) || '[]'); }
  catch { return []; }
}
function writeQueue(q) {
  try { sessionStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-MAX_QUEUE))); }
  catch { /* quota exceeded — drop silently */ }
}

// ─── device info ────────────────────────────────────────────────────
function device() {
  const w = window;
  return {
    ua:       navigator.userAgent,
    language: navigator.language,
    screen:   `${w.screen?.width || 0}x${w.screen?.height || 0}`,
    viewport: `${w.innerWidth}x${w.innerHeight}`,
    referrer: document.referrer || null,
  };
}

// ─── current user (populated by the React shell) ────────────────────
function currentUser() {
  const u = (typeof window !== 'undefined' && window.__CURRENT_USER) || null;
  return u ? { platform_user_id: u.id, email: u.email } : {};
}

// ─── core enqueue ──────────────────────────────────────────────────
function enqueue(event) {
  const q = readQueue();
  q.push({
    event_id:   _uuid(),
    session_id: getSessionId(),
    occurred_at: new Date().toISOString(),
    device:     device(),
    ...currentUser(),
    ...event,
  });
  writeQueue(q);
}

// ─── flush (batch send) ────────────────────────────────────────────
let _flushing = false;
async function flush(useKeepalive = false) {
  if (_flushing) return;
  const q = readQueue();
  if (!q.length) return;
  _flushing = true;
  // Empty the queue optimistically; on failure we requeue up-front
  writeQueue([]);
  const body = JSON.stringify({ events: q });
  try {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    await fetch(TRACK_URL, {
      method: 'POST',
      headers,
      body,
      keepalive: useKeepalive,
    });
  } catch {
    // network failure — put events back at the head of the queue
    const remaining = readQueue();
    writeQueue([...q, ...remaining]);
  } finally {
    _flushing = false;
  }
}

// ─── page-view session state ───────────────────────────────────────
let _currentPage = null;   // { route, name, startedAt }

export function trackPageView(route, name) {
  const now = Date.now();
  // Fire page_exit for the previous page (if any)
  if (_currentPage && _currentPage.route !== route) {
    enqueue({
      event_type: 'page_exit',
      title:      _currentPage.name || _currentPage.route,
      page:       _currentPage.route,
      metadata:   { duration_ms: now - _currentPage.startedAt },
    });
  }
  _currentPage = { route, name, startedAt: now };
  enqueue({
    event_type: 'page_view',
    title:      name || route,
    page:       route,
  });
}

export function trackEvent(eventType, extras = {}) {
  enqueue({ event_type: eventType, ...extras });
}

// ─── auto-listeners ────────────────────────────────────────────────
if (typeof window !== 'undefined' && !window.__vtTrackerInit) {
  window.__vtTrackerInit = true;

  // beforeunload — force a keepalive flush
  window.addEventListener('beforeunload', () => {
    if (_currentPage) {
      enqueue({
        event_type: 'page_exit',
        title:      _currentPage.name || _currentPage.route,
        page:       _currentPage.route,
        metadata:   { duration_ms: Date.now() - _currentPage.startedAt },
      });
    }
    flush(true);
  });

  // popstate + pushState hooks so SPA nav is caught even without React
  const _origPush    = history.pushState;
  const _origReplace = history.replaceState;
  history.pushState    = function () { _origPush.apply(this, arguments);    window.dispatchEvent(new Event('vt-locationchange')); };
  history.replaceState = function () { _origReplace.apply(this, arguments); window.dispatchEvent(new Event('vt-locationchange')); };
  window.addEventListener('popstate',           () => window.dispatchEvent(new Event('vt-locationchange')));
  window.addEventListener('vt-locationchange',  () => trackPageView(location.pathname + location.search));

  // Interval flush
  setInterval(() => flush(false), FLUSH_INTERVAL);
}

export default { trackEvent, trackPageView, flush };
